import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import store from "../redux-store/store";
import config from "../../config";
import { validateStatus } from "./errorHandling";
import {
  FetchActionsResponse,
  FetchEventsResponse,
  FetchRoleResponse,
  FetchSiteResponse,
  FetchTemesResponse,
  FetchUsersForFieldResponse,
  FetchUsersResponse,
  UserCreateResponse,
  UserUpdateResponse,
} from "../../types/apiResponsesTypes";
import { FormUserValues } from "../../types/user";
import {
  Action,
  FormActionPostValues,
  FormActionValidatePostValues,
  FormMarkAsPerformedPostValues,
} from "../../types/actions";
import {
  Event,
  FormEventCauseAnalysisPostValues,
  FormEventDispatchPilotePostValues,
  FormEventPostValues,
  FormEventValidatePostValues,
} from "../../types/events";
import { CauseAnalysis } from "../../types/causeAnalysis";
import { RoleName } from "../../types/role";
import { DashboardStatistics } from "../../types/dashboardStatistics";

/**
 * Links
 * redux-toolkit uses RTK Query
 * https://redux-toolkit.js.org/rtk-query/overview
 * https://redux-toolkit.js.org/rtk-query/api/created-api/hooks
 */

/**
 * createApi doc: https://redux-toolkit.js.org/rtk-query/api/createApi
 */
export const apiSlice = createApi({
  reducerPath: "api",
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: config.apiUrl,
    prepareHeaders(headers, { getState }) {
      const state = (getState as typeof store.getState)();
      const {
        auth: { user },
      } = state;

      headers.set("Authorization", `Bearer ${user?.access_token || ""}`);
      // headers.set("appVersion", config.appVersion);
      // headers.set("environment", config.env);

      return headers;
    },
  }),
  endpoints(builders) {
    return {
      // [requestName]: builders.query<ResponseType, ArgumentType>(
      /**
       * Events
       */
      fetchEvents: builders.query<FetchEventsResponse, void>({
        query: () => ({
          url: `evt/events`,
          validateStatus,
        }),
        // transformResponse(response: FetchGlobalDataResponse, meta, arg) {
        //   return response;
        // },
        // transformErrorResponse: (response, meta, arg) => {
        //   return response;
        // },
      }),
      fetchEvent: builders.query<Event, number>({
        query: (id: number) => ({
          url: `evt/events/${id}`,
          validateStatus,
          cache: "no-cache",
        }),
      }),
      fetchPaginatedEvents: builders.query<FetchEventsResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/events?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchPaginatedDashboardEvents: builders.query<
        FetchEventsResponse,
        string | void
      >({
        query: (queryParams?: string) => ({
          url: `evt/dashboard/events?${queryParams}`,
          validateStatus,
        }),
      }),
      eventCreate: builders.mutation<Event, FormEventPostValues>({
        query: (values) => {
          return {
            url: "evt/events",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      eventUpdate: builders.mutation<
        Event,
        {
          eventId: number;
          values: FormEventPostValues;
        }
      >({
        query: ({ eventId, values }) => {
          return {
            url: `evt/events/${eventId}`,
            method: "PATCH",
            validateStatus,
            body: values,
          };
        },
      }),
      eventUploadAttachments: builders.mutation<
        Event["attachedFiles"],
        {
          eventId: number;
          files: File[];
        }
      >({
        query: ({ eventId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/events/${eventId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      eventDispatchPilote: builders.mutation<
        Event,
        {
          eventId: number;
          values: FormEventDispatchPilotePostValues;
        }
      >({
        query: ({ eventId, values }) => {
          return {
            url: `evt/events/${eventId}/dispatchPilote`,
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      eventAskValidation: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}/ask-validation`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventValidateOrRefuse: builders.mutation<
        Event,
        FormEventValidatePostValues & {
          validateOrRefuse: "validate" | "refused";
        }
      >({
        query: ({ eventId, validateOrRefuse, comment }) => {
          const slug = validateOrRefuse === "validate" ? "close" : "refuse";
          return {
            url: `evt/events/${eventId}/${slug}?comment=${comment}`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventReopen: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}/reopen`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventArchive: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}`,
            method: "DELETE",
            validateStatus,
            body: {},
          };
        },
      }),
      /**
       * Event - Cause analysis
       */
      createCauseAnalysis: builders.mutation<
        CauseAnalysis,
        FormEventCauseAnalysisPostValues
      >({
        query: (values) => {
          return {
            url: "evt/cause-analysis",
            method: "POST",
            validateStatus,
            body: {
              description: values.description,
              pilote: values.pilote,
              event: values.event,
            },
          };
        },
      }),
      causeUploadAttachments: builders.mutation<
        void,
        {
          causeId: number;
          files: File[];
        }
      >({
        query: ({ causeId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/cause-analysis/${causeId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),

      /**
       * Actions
       */
      fetchPaginatedActions: builders.query<
        FetchActionsResponse,
        string | undefined
      >({
        query: (queryParams?: string) => ({
          url: `evt/actions?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchPaginatedDashboardActions: builders.query<
        FetchActionsResponse,
        string | undefined
      >({
        query: (queryParams?: string) => ({
          url: `evt/dashboard/actions?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchAction: builders.query<Action, string | undefined>({
        query: (id?: string) => ({
          url: `evt/actions/${id}`,
          validateStatus,
        }),
      }),
      actionCreate: builders.mutation<Action, FormActionPostValues>({
        query: (values) => {
          return {
            url: "evt/actions",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      actionUpdate: builders.mutation<
        Action,
        {
          actionId: number;
          values: FormActionPostValues;
        }
      >({
        query: ({ actionId, values }) => {
          return {
            url: `evt/actions/${actionId}`,
            method: "PATCH",
            validateStatus,
            body: values,
          };
        },
      }),
      actionDelete: builders.mutation<void, number>({
        query: (actionId) => {
          return {
            url: `evt/actions/${actionId}`,
            method: "DELETE",
            validateStatus,
          };
        },
      }),

      actionUploadAttachments: builders.mutation<
        Action["attachedFiles"],
        {
          actionId: number;
          files: File[];
        }
      >({
        query: ({ actionId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/actions/${actionId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      actionPerformed: builders.mutation<Action, FormMarkAsPerformedPostValues>(
        {
          query: ({ actionId, dateDone, comment }) => {
            return {
              url: `evt/actions/${actionId}/performed?comment=${comment}&dateDone${dateDone}`,
              method: "PATCH",
              validateStatus,
              body: {},
            };
          },
        },
      ),
      actionValidateOrRefuse: builders.mutation<
        Action,
        FormActionValidatePostValues & {
          validateOrRefuse: "validate" | "refused";
        }
      >({
        query: ({ actionId, validateOrRefuse, comment }) => {
          return {
            url: `evt/actions/${actionId}/${validateOrRefuse}?comment=${comment}`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      /**
       * Users
       */
      fetchUsers: builders.query<FetchUsersResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/users?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchUsersForField: builders.query<
        FetchUsersForFieldResponse,
        "" | RoleName
      >({
        query: (role) => ({
          url: role ? `evt/users/list?role=${role}` : `evt/users/list`,
          validateStatus,
        }),
      }),
      userCreate: builders.mutation<UserCreateResponse, FormUserValues>({
        query: (values) => {
          return {
            url: "evt/users",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      userUpdate: builders.mutation<
        UserUpdateResponse,
        {
          userId: string;
          values: FormUserValues;
        }
      >({
        query: ({ userId, values }) => {
          return {
            url: `evt/users/${userId}`,
            method: "PUT",
            validateStatus,
            body: values,
          };
        },
      }),
      userDelete: builders.mutation<void, number>({
        query: (userId) => {
          return {
            url: `evt/users/${userId}`,
            method: "DELETE",
            validateStatus,
          };
        },
      }),
      /**
       * Other (status, role, site)
       */
      fetchDashboardStatistics: builders.query<DashboardStatistics, void>({
        query: () => ({
          url: `evt/dashboard/stats`,
          validateStatus,
          // cache: "no-cache",
        }),
      }),
      fetchRoles: builders.query<FetchRoleResponse, void>({
        query: () => ({
          url: `evt/roles`,
          validateStatus,
          // cache: "no-cache",
        }),
      }),
      fetchSites: builders.query<FetchSiteResponse, void>({
        query: () => ({
          url: `evt/sites`,
          validateStatus,
        }),
      }),
      fetchThemes: builders.query<FetchTemesResponse, void>({
        query: () => ({
          url: `evt/themes`,
          validateStatus,
        }),
      }),
      //   tiggerError404: builders.query<FetchGlobalDataResponse, void>({
      //     query: () => ({
      //       url: `BAD_URL_FOR_404`,
      //       validateStatus,
      //     }),
      //   }),
      //   tiggerError401: builders.query<FetchGlobalDataResponse, void>({
      //     query: () => ({
      //       url: `provoke-unauthorized`,
      //       validateStatus,
      //     }),
      //   }),
    };
  },
});

export const {
  // Events
  useFetchEventQuery,
  useEventCreateMutation,
  useEventUpdateMutation,
  useLazyFetchEventsQuery,
  useLazyFetchPaginatedEventsQuery,
  useLazyFetchPaginatedDashboardEventsQuery,
  useEventUploadAttachmentsMutation,
  useCauseUploadAttachmentsMutation,
  useEventDispatchPiloteMutation,
  useCreateCauseAnalysisMutation,
  useEventAskValidationMutation,
  useEventValidateOrRefuseMutation,
  useEventArchiveMutation,
  useEventReopenMutation,
  // Actions
  useLazyFetchPaginatedActionsQuery,
  useLazyFetchPaginatedDashboardActionsQuery,
  useFetchActionQuery,
  useActionCreateMutation,
  useActionUpdateMutation,
  useActionDeleteMutation,
  useActionUploadAttachmentsMutation,
  useActionPerformedMutation,
  useActionValidateOrRefuseMutation,
  // Users
  useUserCreateMutation,
  useFetchUsersQuery,
  useFetchUsersForFieldQuery,
  useLazyFetchUsersQuery,
  useUserUpdateMutation,
  useUserDeleteMutation,
  // Other
  useFetchDashboardStatisticsQuery,
  useFetchRolesQuery,
  useFetchSitesQuery,
  useFetchThemesQuery,
} = apiSlice;
